import { jsonrepair } from 'jsonrepair';

/**
 * Parses the data property of an object or array of objects if it exists as a JSON string
 * If the data property is a valid JSON string, it merges the parsed data with the rest of the object
 * If parsing fails, attempts to repair the JSON using jsonrepair before trying again
 * If all parsing attempts fail or there is no data property, returns the original object
 */
export const parseDataProperty = <T extends Record<string, any> | Record<string, any>[]>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(item => parseDataProperty(item)) as T;
  }

  if (obj) {
    // Find any 'data' property regardless of case (data, Data, DATA, etc)
    const dataKey = Object.keys(obj).find(key =>
      key.toLowerCase() === 'data'
    );

    if (dataKey) {
      try {
        // Pre-process the string to handle NaN
        const processedString = typeof obj[dataKey] === 'string'
          ? obj[dataKey].replace(/:\s*NaN/g, ':"NaN"')
          : obj[dataKey];

        const parsedData = JSON.parse(processedString);
        const rest = Object.fromEntries(
          Object.entries(obj).filter(([key]) => key !== dataKey)
        );

        // Handle case where data property itself is an array
        if (Array.isArray(parsedData)) {
          return {
            ...rest,
            [dataKey]: parsedData.map(item => parseDataProperty(item))
          } as T;
        }

        // Filter out properties that have NaN values
        const processedData = Object.fromEntries(
          Object.entries(parsedData)
            .filter(([_, value]) => value !== "NaN")
        );

        return {
          ...rest,
          ...processedData
        } as T;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
        console.warn('Initial JSON parsing failed, attempting to repair:', errorMessage, obj[dataKey]);
        
        // Try to repair the JSON and parse again
        try {
          const processedString = typeof obj[dataKey] === 'string'
            ? obj[dataKey].replace(/:\s*NaN/g, ':"NaN"')
            : obj[dataKey];
          
          const repairedJson = jsonrepair(processedString);
          const parsedData = JSON.parse(repairedJson);
          const rest = Object.fromEntries(
            Object.entries(obj).filter(([key]) => key !== dataKey)
          );

          console.info('JSON successfully repaired and parsed', parsedData);

          // Handle case where data property itself is an array
          if (Array.isArray(parsedData)) {
            return {
              ...rest,
              [dataKey]: parsedData.map(item => parseDataProperty(item))
            } as T;
          }

          // Filter out properties that have NaN values
          const processedData = Object.fromEntries(
            Object.entries(parsedData)
              .filter(([_, value]) => value !== "NaN")
          );

          return {
            ...rest,
            ...processedData
          } as T;
        } catch (repairError) {
          const repairErrorMessage = repairError instanceof Error ? repairError.message : 'Unknown repair error';
          console.error('JSON repair also failed:', repairErrorMessage);
          console.error('Original data:', obj[dataKey]);
          return obj;
        }
      }
    }
  }
  return obj;
};

/**
 * Attempts to parse JSON, with automatic repair fallback if parsing fails
 * @param jsonString - The JSON string to parse
 * @returns Parsed object or null if both parsing and repair fail
 */
export const parseJsonWithRepair = (jsonString: string): any => {
  try {
    // First attempt: standard JSON.parse
    return JSON.parse(jsonString);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
    console.warn('Initial JSON parsing failed, attempting to repair:', errorMessage);
    
    try {
      // Second attempt: repair then parse
      const repairedJson = jsonrepair(jsonString);
      const result = JSON.parse(repairedJson);
      console.info('JSON successfully repaired and parsed');
      return result;
    } catch (repairError) {
      const repairErrorMessage = repairError instanceof Error ? repairError.message : 'Unknown repair error';
      console.error('JSON repair also failed:', repairErrorMessage);
      console.error('Original JSON string:', jsonString);
      return null;
    }
  }
};
